import { getDiscipline } from "@shared/labs/disciplines/registry";
import type { DisciplineSpec, ResolvedFloors } from "@shared/labs/disciplines/types";
import type {
  CorrectionCallback,
  FrameworkSkill,
  SkillBullet,
  ThreeAxisRecommendation,
} from "@shared/labs/three-axis";
import { parseLevel, readRecommendationFloors } from "@shared/labs/three-axis";
import { useEffect, useRef, useState } from "react";
import { AjustarPanel } from "./ajustar-panel";
import { formatScoresAsDots } from "./format-scores";
import { Radar } from "./radar";

export interface SkillsSectionProps {
  rec: ThreeAxisRecommendation;
  onCorrection?: CorrectionCallback;
  discipline?: string;
}

type LevelKey = "L1" | "L2" | "L3" | "L4" | "L5" | "L6";

/** Candidate's observed proficiency (0–5) for a skill. Honors an explicit
 *  `assignedDots` when the LLM provides one; otherwise falls back to the
 *  framework's authored score for the assigned level, then to the bare level
 *  digit. */
function candidateDotsFor(skill: FrameworkSkill): number {
  if (typeof skill.assignedDots === "number") {
    return Math.max(0, Math.min(5, skill.assignedDots));
  }
  const metaScore = skill.levelMeta?.[skill.assigned]?.score;
  if (typeof metaScore === "number") return Math.max(0, Math.min(5, metaScore));
  return parseLevel(skill.assigned);
}

/** Five-dot proficiency rating. Coral fill marks "this is the candidate's
 *  dots"; gray dots are empty. Mirrors the reference glyph used on the
 *  framework page so reviewers can eyeball the score without reading a number. */
function RatingDots({ score, total = 5 }: { score: number; total?: number }) {
  const clamped = Math.max(0, Math.min(total, Math.round(score)));
  const label = `proficiência do candidato: ${clamped} de ${total}`;
  return (
    <span className="lvl-rating lvl-rating-assigned" role="img" aria-label={label} title={label}>
      <span className="lvl-rating-dots" aria-hidden>
        {Array.from({ length: total }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rating dots have a fixed ordinal position
          <span key={i} className={`lvl-rating-dot${i < clamped ? " on" : " off"}`} />
        ))}
      </span>
      <span className="lvl-rating-num" aria-hidden>
        {clamped}/{total}
      </span>
    </span>
  );
}

/** Sem-sinais-suficientes copy — shown when a skill lands at 0 dots. The
 *  per-skill framework markdown also authors an L0 block, but rendering a
 *  single consistent message here keeps the empty state from drifting per
 *  skill. */
const INSUFFICIENT_SIGNALS_BODY =
  "Não há sinais suficientes no perfil para ancorar o candidato nem em L1 desta skill. Reservado para quando o histórico não expõe situações onde a skill seria exercida.";

/** Strip the LLM's leading ↑/↓ polarity marker from an observation quote.
 *  The column the quote renders in already carries polarity via color, so the
 *  arrow becomes redundant chrome inside the green/coral box. */
function stripPolarityMarker(text: string): string {
  const m = text.match(/^\s*[↑↓]\s+([\s\S]*)$/);
  return m ? m[1] : text;
}

function EvidenceQuote({ source }: { source: { quote: string; citation: string } }) {
  return (
    <div className="qual-evid">
      <span className="qual-evid-q">"{stripPolarityMarker(source.quote)}"</span>
      <span className="qual-evid-src">{source.citation}</span>
    </div>
  );
}

function HasItem({ bullet }: { bullet: SkillBullet }) {
  return (
    <li className="qual-item">
      <p className="qual">{bullet.text}</p>
      {bullet.source && <EvidenceQuote source={bullet.source} />}
    </li>
  );
}

function GapItem({ bullet }: { bullet: SkillBullet }) {
  return (
    <li className="qual-item">
      <p className="qual">{bullet.text}</p>
      {bullet.gapRationale && (
        <div className="qual-evid qual-evid-gap">
          <span className="qual-evid-q">{bullet.gapRationale}</span>
        </div>
      )}
    </li>
  );
}

interface SkillRowProps {
  skill: FrameworkSkill;
  /** Role level the candidate was placed at after per-skill floor checks.
   *  Drives the "core · alvo L{N}" target pill. */
  appliedLevel: 1 | 2 | 3 | 4 | 5 | 6;
  /** Hire-floors used by the gate. Matches the radar's role polygon. */
  floors: ResolvedFloors;
  /** Resolved discipline spec — classifies skills as core / informational /
   *  outside-discipline. */
  discipline?: DisciplineSpec;
  onCorrection?: SkillsSectionProps["onCorrection"];
}

function SkillRow({ skill, appliedLevel, floors, discipline, onCorrection }: SkillRowProps) {
  const assignedNum = parseLevel(skill.assigned);
  const assignedDots = candidateDotsFor(skill);
  const assignedKey = `L${assignedNum}` as LevelKey;
  const levelTitle = skill.levelMeta?.[assignedKey]?.title ?? "";

  // Classification under the discipline. Drives the right-side pill:
  //   - core skills → "core · alvo L{N}: {dots}/5" (informative target, not a gate)
  //   - informational → "informativa · não condiciona o nível"
  //   - outside discipline → "esperado N/5" / "abaixo do esperado · mínimo N"
  const isInDiscipline = discipline?.skillIds.includes(skill.id) ?? false;
  const appliedLevelKey = `L${appliedLevel}` as LevelKey;
  const expectedDots =
    isInDiscipline && discipline
      ? discipline.requiredDotsAtLevel(skill.id, appliedLevel, floors)
      : (skill.levelMeta?.[appliedLevelKey]?.score ?? skill.levelMins?.[appliedLevelKey]);
  const isCore = isInDiscipline && (discipline?.coreSkillIds.includes(skill.id) ?? false);
  const isInformational =
    isInDiscipline && (discipline?.informationalSkillIds.includes(skill.id) ?? false);
  const meetsExpected = expectedDots === undefined ? true : assignedDots >= expectedDots;

  // Observations are the qualitative, profile-anchored bullets the LLM emits
  // alongside the abstract framework criteria. They're what carries
  // ground-truth signal — "20+ anos cruzando sub-funções" + a quote, vs.
  // "É a pessoa de referência para julgamento técnico". The two columns
  // below split observations by polarity. Framework bullets remain auditable
  // via the details drawer at the bottom.
  const observations = skill.bullets.filter((b) => b.kind === "observation");
  const observationsHas = observations.filter((b) => b.met);
  const observationsGap = observations.filter((b) => !b.met);

  // Framework criteria for the assigned level — surfaced collapsed so the
  // rubric mapping stays auditable even when the primary view is qualitative.
  const frameworkAtAssigned = skill.bullets.filter(
    (b) => b.kind === "framework" && parseLevel(b.level) === assignedNum,
  );
  const frameworkMet = frameworkAtAssigned.filter((b) => b.met).length;
  const frameworkTotal = frameworkAtAssigned.length;

  const [adjusting, setAdjusting] = useState(false);
  const adjustPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (adjusting && adjustPanelRef.current) {
      adjustPanelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [adjusting]);

  return (
    <div className="skill-row" data-testid={`skill-row-${skill.id}`}>
      <div className="skill-head-row">
        <div className="skill-head-left">
          <div className="skill-name">{skill.name}</div>
          {levelTitle && (
            <span className="skill-level-pill" data-testid="skill-level-pill">
              {levelTitle}
            </span>
          )}
          <RatingDots score={assignedDots} />
        </div>
        <div className="skill-head-right">
          {isCore && expectedDots !== undefined && (
            <span
              className="skill-class-pill skill-class-pill-core"
              data-tooltip={`Skill core. O nível L${appliedLevel} é decidido pela regra de compensação (máx + cobertura) entre as três cores — esta nota é apenas o alvo de rampa.`}
            >
              core · alvo L{appliedLevel}: {expectedDots}/5
            </span>
          )}
          {isInformational && (
            <span
              className="skill-class-pill skill-class-pill-info"
              data-tooltip="HR Fluency e Product Sense são informativas no framework FDE — não condicionam o nível atribuído."
            >
              informativa
            </span>
          )}
          {!isInDiscipline && expectedDots !== undefined && (
            <span
              className={`skill-class-pill${meetsExpected ? "" : " skill-class-pill-below"}`}
              title={
                meetsExpected
                  ? `Esta skill pede ${expectedDots}/5 no nível avaliado. O candidato tem ${assignedDots}/5 — dentro do esperado.`
                  : `Esta skill pede ${expectedDots}/5 no nível avaliado. O candidato tem ${assignedDots}/5 — abaixo do que o nível pede.`
              }
            >
              {meetsExpected
                ? `esperado ${expectedDots}/5`
                : `abaixo do esperado · mínimo ${expectedDots}/5`}
            </span>
          )}
          {onCorrection && (
            <button
              type="button"
              className="lvl-adjust-tag"
              aria-label={`ajustar proficiência de ${assignedDots}/5`}
              aria-expanded={adjusting}
              onClick={() => setAdjusting((v) => !v)}
            >
              <span className="lvl-adjust-label">Ajustar</span>
            </button>
          )}
        </div>
      </div>

      {skill.rationale && (
        <p className="skill-rationale" data-testid="skill-rationale">
          {formatScoresAsDots(skill.rationale)}
        </p>
      )}

      {assignedDots === 0 ? (
        <div className="skill-no-signal" data-testid="skill-no-signal">
          <strong>Sem sinais suficientes.</strong> {INSUFFICIENT_SIGNALS_BODY}
        </div>
      ) : (
        <div className="skill-evidence-two-col">
          <div className="skill-evidence-col skill-evidence-has" data-testid="skill-evidence-has">
            <div className="skill-evidence-col-head">
              <h4>Tem evidência de</h4>
              <span className="skill-evidence-col-count">
                {observationsHas.length} {observationsHas.length === 1 ? "sinal" : "sinais"}
              </span>
            </div>
            {observationsHas.length === 0 ? (
              <div className="skill-evidence-empty">
                Sem observações positivas extraídas do perfil. O nível foi inferido pelo conjunto da
                trajetória — ver critérios do framework abaixo.
              </div>
            ) : (
              <ul className="qual-list">
                {observationsHas.map((b) => (
                  <HasItem key={b.id} bullet={b} />
                ))}
              </ul>
            )}
          </div>
          <div className="skill-evidence-col skill-evidence-gap" data-testid="skill-evidence-gap">
            <div className="skill-evidence-col-head">
              <h4>Não tem evidência de</h4>
              <span className="skill-evidence-col-count">
                {observationsGap.length} {observationsGap.length === 1 ? "lacuna" : "lacunas"}
              </span>
            </div>
            {observationsGap.length === 0 ? (
              <div className="skill-evidence-empty">
                Sem lacunas observadas no nível atribuído. Para subir, ver os critérios de níveis
                superiores no framework.
              </div>
            ) : (
              <ul className="qual-list">
                {observationsGap.map((b) => (
                  <GapItem key={b.id} bullet={b} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {frameworkTotal > 0 && (
        <details className="skill-framework-details" data-testid="skill-framework-details">
          <summary>
            Critérios do framework para {levelTitle || `L${assignedNum}`}
            <span className="skill-framework-summary-count">
              {frameworkMet}/{frameworkTotal} atendidos
            </span>
          </summary>
          <ul className="skill-framework-list">
            {frameworkAtAssigned.map((b) => (
              <li key={b.id} className={`skill-framework-item${b.met ? " met" : " miss"}`}>
                <span className={`skill-framework-mk${b.met ? " ok" : " miss"}`}>
                  {b.met ? "cumprido" : "lacuna"}
                </span>
                <span className="skill-framework-text">{b.text}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {adjusting && onCorrection && (
        <div ref={adjustPanelRef}>
          <AjustarPanel
            kind="skill"
            target={skill.name}
            current={assignedDots}
            scale="0-5"
            onSave={async (newScore, rationale, opts) => {
              await onCorrection({
                evidenceId: skill.id,
                newScore,
                rationale,
                kind: "skill",
                policy: opts.policy,
              });
              setAdjusting(false);
            }}
            onCancel={() => setAdjusting(false)}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Renders the FDE level rule (max + breadth across core skills) and how the
 * candidate maps onto it. Shown above the skill cards so the reviewer can
 * read, in plain Portuguese, why the candidate landed at L{appliedLevel}
 * and which conditions they meet.
 *
 * FDE-specific by design — the copy references PM / Agency / Business
 * Acumen by name and encodes the "max + breadth on 3 cores" policy ratified
 * by the CHRO on 2026-05-08. New disciplines need their own card (or none).
 * `SkillsSection` is responsible for not rendering this for non-FDE
 * recommendations; the component itself trusts its caller's gating.
 */
function FdeLevelRuleCard({
  skills,
  appliedLevel,
  discipline,
}: {
  skills: FrameworkSkill[];
  appliedLevel: 1 | 2 | 3 | 4 | 5 | 6;
  discipline: DisciplineSpec;
}) {
  const coreScores = new Map<string, { name: string; dots: number }>();
  for (const s of skills) {
    if (discipline.coreSkillIds.includes(s.id)) {
      coreScores.set(s.id, { name: s.name, dots: candidateDotsFor(s) });
    }
  }
  // Need every core skill present to apply the rule. If the discipline
  // shipped here without one of its core skills, bail quietly.
  if (coreScores.size < discipline.coreSkillIds.length) return null;

  const coreEntries = discipline.coreSkillIds.map((id) => coreScores.get(id)!);
  const coreVals = coreEntries.map((c) => c.dots);
  const maxCore = Math.max(...coreVals);
  const countAtCompetency = coreVals.filter((v) => v >= 3).length;
  const scoreRecord: Record<string, number> = {};
  for (const id of discipline.coreSkillIds) {
    scoreRecord[id] = coreScores.get(id)!.dots;
  }
  const impliedLevel = discipline.impliedLevelFromCoreSkills(scoreRecord);

  // FDE-specific copy: pm/sm/ba by well-known skill IDs.
  const pm = coreScores.get("project_management")!;
  const sm = coreScores.get("stakeholder_management")!;
  const ba = coreScores.get("business_acumen")!;

  // The implied level can sit ABOVE the appliedLevel when the candidate
  // over-qualifies for the role (rule says L5; the role only asked for L4 →
  // appliedLevel is capped at L4). Surface that as a small note so reviewers
  // see the over-qualification signal. It can also sit BELOW for legacy
  // recommendations stored before the runtime gate adopted the rule — same
  // note copy, the divergence is informational either way.
  const impliedDiffersFromApplied = impliedLevel !== appliedLevel;

  const condExceptional = maxCore >= 4;
  const condBreadth = countAtCompetency >= 2;

  return (
    <div className="level-rule-card" data-testid="level-rule-card">
      <div className="lrc-head">
        <div className="lrc-eyebrow">Como o nível é decidido pelas skills</div>
        <h3 className="lrc-title">
          Regra atual · skills core (PM, Agency to influence stakeholders, Business Acumen)
        </h3>
        <p className="lrc-lede">
          O nível é decidido por <strong>máx</strong> e <strong>cobertura</strong> entre as três
          core. HR Fluency e Product Sense são informativas — não condicionam.
        </p>
      </div>

      <ul className="lrc-rule-grid">
        <li className={`lrc-rule${impliedLevel === 5 ? " match" : ""}`}>
          <span className="lrc-rule-level">L5</span>
          <span className="lrc-rule-cond">
            ≥1 core em 4/5+ <strong>E</strong> ≥2 cores em 3/5+
          </span>
        </li>
        <li className={`lrc-rule${impliedLevel === 4 ? " match" : ""}`}>
          <span className="lrc-rule-level">L4</span>
          <span className="lrc-rule-cond">
            ≥1 core em 4/5+ <strong>OU</strong> ≥2 cores em 3/5+
          </span>
        </li>
        <li className={`lrc-rule${impliedLevel === 3 ? " match" : ""}`}>
          <span className="lrc-rule-level">L3</span>
          <span className="lrc-rule-cond">caso contrário</span>
        </li>
      </ul>

      <div className="lrc-status">
        <div className="lrc-status-label">Status do candidato</div>
        <ul className="lrc-status-list">
          <li>
            <span className="lrc-status-skill">{pm.name}</span>
            <span className="lrc-status-dots">{pm.dots}/5</span>
          </li>
          <li>
            <span className="lrc-status-skill">{sm.name}</span>
            <span className="lrc-status-dots">{sm.dots}/5</span>
          </li>
          <li>
            <span className="lrc-status-skill">{ba.name}</span>
            <span className="lrc-status-dots">{ba.dots}/5</span>
          </li>
        </ul>

        <ul className="lrc-checks">
          <li className={condExceptional ? "ok" : "miss"}>
            <span className="lrc-check-mark" aria-hidden>
              {condExceptional ? "atende" : "abaixo"}
            </span>
            <span className="lrc-check-body">
              <span className="lrc-check-label">máximo nas cores</span>
              <span className="lrc-check-value">{maxCore}/5</span>
              {condExceptional ? (
                <span className="lrc-check-detail ok">atinge 4/5 (excepcional)</span>
              ) : (
                <span className="lrc-check-detail">
                  falta <strong>{Math.max(0, 4 - maxCore)} ponto(s)</strong> para 4/5
                </span>
              )}
            </span>
          </li>
          <li className={condBreadth ? "ok" : "miss"}>
            <span className="lrc-check-mark" aria-hidden>
              {condBreadth ? "atende" : "abaixo"}
            </span>
            <span className="lrc-check-body">
              <span className="lrc-check-label">cores em 3/5+</span>
              <span className="lrc-check-value">{countAtCompetency} de 3</span>
              {condBreadth ? (
                <span className="lrc-check-detail ok">atinge ≥2 (cobertura)</span>
              ) : (
                <span className="lrc-check-detail">
                  falta <strong>{Math.max(0, 2 - countAtCompetency)}</strong> para cobertura
                </span>
              )}
            </span>
          </li>
        </ul>
      </div>

      <div className="lrc-verdict">
        <div className="lrc-verdict-label">Nível implícito pela regra</div>
        <span className={`lrc-implied-level lrc-implied-l${impliedLevel}`}>L{impliedLevel}</span>
        {impliedDiffersFromApplied && (
          <span className="lrc-verdict-note">
            {impliedLevel > appliedLevel
              ? `acima do alvo do papel (L${appliedLevel}) — candidato sobre-qualifica`
              : `nível atribuído ao papel: L${appliedLevel}`}
          </span>
        )}
      </div>
    </div>
  );
}

export function SkillsSection({
  rec,
  onCorrection,
  discipline: disciplineLabel,
}: SkillsSectionProps) {
  // `rec.appliedLevel` is "L1"…"L6" (role level). Skills `parseLevel` only
  // accepts L1–L5 (per-skill cap), so parse the role-level digit directly.
  const appliedLevelNum = Number(rec.appliedLevel.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6;
  // Resolve the recommendation's discipline spec. Fallback to forward-deployed
  // for legacy snapshots predating the disciplineSlug field.
  const spec = getDiscipline(rec.disciplineSlug ?? "forward-deployed");
  // Floors used by the gate (rec.floors for post-2026-05-12 evaluations, or
  // legacy rec.fdeFloors). Fallback to the discipline's canonical constants.
  const floors: ResolvedFloors = readRecommendationFloors(rec) ?? spec?.floorsConstants ?? {};
  return (
    <div className="card" id="axis-skills">
      <div className="card-head">
        <div className="card-head-left">
          <h3>Skills · framework {disciplineLabel ?? "FDE"}</h3>
          <div className="sub">
            Para cada skill: o que aparece no perfil e o que ainda não aparece, com a citação que
            ancora cada sinal. Os critérios formais do framework ficam disponíveis no detalhe ao
            final de cada skill para auditoria.
          </div>
        </div>
        <span className="pill muted">{rec.appliedLevel} atribuído</span>
      </div>
      <div className="card-body padded">
        {spec?.slug === "forward-deployed" && (
          <FdeLevelRuleCard
            skills={rec.frameworkSkills}
            appliedLevel={appliedLevelNum}
            discipline={spec}
          />
        )}
        <div className="skills-grid">
          <div className="radar-wrap">
            <Radar
              skills={rec.frameworkSkills}
              appliedLevel={appliedLevelNum}
              roleTitle={disciplineLabel}
              floors={floors}
              discipline={spec}
            />
          </div>
          <div className="skills-list">
            {rec.frameworkSkills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                appliedLevel={appliedLevelNum}
                floors={floors}
                discipline={spec}
                onCorrection={onCorrection}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
